/**
 * Firebase Service
 * Handles Firebase Authentication
 */

import { initializeApp } from 'firebase/app';
import {
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { firebaseConfig } from '../config.js';

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Analytics (only in browser and if supported)
let analytics = null;
isSupported().then(supported => {
    if (supported) {
        analytics = getAnalytics(app);
    }
});

// Initialize Auth
const auth = getAuth(app);

// Google Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

/**
 * Firebase Auth Service
 */
export const firebaseAuth = {
    /**
     * Get current user
     */
    getCurrentUser() {
        return auth.currentUser;
    },

    /**
     * Get current user's ID token
     */
    async getIdToken() {
        const user = auth.currentUser;
        if (!user) return null;
        return await user.getIdToken();
    },

    /**
     * Register with email and password
     */
    async registerWithEmail(email, password) {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const idToken = await userCredential.user.getIdToken();
            return {
                success: true,
                user: userCredential.user,
                idToken
            };
        } catch (error) {
            return {
                success: false,
                error: this.getErrorMessage(error.code)
            };
        }
    },

    /**
     * Login with email and password
     */
    async loginWithEmail(email, password) {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const idToken = await userCredential.user.getIdToken();
            return {
                success: true,
                user: userCredential.user,
                idToken
            };
        } catch (error) {
            return {
                success: false,
                error: this.getErrorMessage(error.code)
            };
        }
    },

    /**
     * Login with Google
     */
    async loginWithGoogle() {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const idToken = await result.user.getIdToken();
            return {
                success: true,
                user: result.user,
                idToken
            };
        } catch (error) {
            // Handle popup closed by user
            if (error.code === 'auth/popup-closed-by-user') {
                return {
                    success: false,
                    error: 'Has cerrado la ventana de login. Inténtalo de nuevo.'
                };
            }
            return {
                success: false,
                error: this.getErrorMessage(error.code)
            };
        }
    },

    /**
     * Send password reset email
     */
    async sendPasswordReset(email) {
        try {
            await sendPasswordResetEmail(auth, email);
            return {
                success: true
            };
        } catch (error) {
            return {
                success: false,
                error: this.getErrorMessage(error.code)
            };
        }
    },

    /**
     * Logout
     */
    async logout() {
        try {
            await signOut(auth);
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: 'Error al cerrar sesión'
            };
        }
    },

    /**
     * Subscribe to auth state changes
     */
    onAuthStateChanged(callback) {
        return onAuthStateChanged(auth, callback);
    },

    /**
     * Get user-friendly error message
     */
    getErrorMessage(errorCode) {
        const errors = {
            'auth/email-already-in-use': 'Este email ya está registrado. ¿Quieres iniciar sesión?',
            'auth/invalid-email': 'El email no es válido',
            'auth/operation-not-allowed': 'Operación no permitida',
            'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres',
            'auth/user-disabled': 'Esta cuenta ha sido deshabilitada',
            'auth/user-not-found': 'No existe una cuenta con este email',
            'auth/wrong-password': 'Contraseña incorrecta',
            'auth/invalid-credential': 'Credenciales inválidas. Verifica tu email y contraseña.',
            'auth/too-many-requests': 'Demasiados intentos. Espera un momento antes de volver a intentarlo.',
            'auth/network-request-failed': 'Error de conexión. Verifica tu conexión a internet.',
            'auth/popup-blocked': 'El popup fue bloqueado. Permite popups para este sitio.',
            'auth/cancelled-popup-request': 'Operación cancelada',
            'auth/account-exists-with-different-credential': 'Ya existe una cuenta con este email usando otro método de login.'
        };
        return errors[errorCode] || 'Ha ocurrido un error. Inténtalo de nuevo.';
    }
};

export { auth, analytics };
